import { useState, useEffect, useMemo, useRef } from 'react';
import { fmt, Spinner } from './UI';
import {
  getPaymentUploads,
  getPaymentRiders,
  updatePaymentRiderFields,
  updateRiderAccountDetails,
  updatePaymentRiderStatus,
  updatePaymentRiderLock,
  updatePaymentRiderHold,
  createPaymentUpload,
  insertPaymentRiders,
  deletePaymentUpload,
  deletePaymentRiders,
  syncPaymentRidersFromSheet,
  markUploadSynced,
  backfillRidersToSheet,
  logPaymentAction,
  getPaymentAuditLog,
} from '../lib/supabase';
import { parsePaymentSheet, parsePaymentSheetRows, exportPaymentRidersToExcel, exportAuditLogToExcel } from '../lib/parser';
import { supabase } from '../lib/supabase';

// ── Helpers ───────────────────────────────────────────────────────────────────

function accountComplete(r) {
  return !!(r.acct_number && r.bank_name && r.ifsc_code);
}

function sameHub(a, b) {
  return String(a || '').trim().toUpperCase() === String(b || '').trim().toUpperCase();
}

// Human-friendly "time since last sync" for the Sync button's helper text.
function formatSyncTime(iso) {
  if (!iso) return null;
  const then = new Date(iso);
  const diffMin = Math.floor((Date.now() - then.getTime()) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  return then.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

// Final Amount = Amount − Penalty − Advance − COD Loss − Shipment Loss − Shipment Loss 2 − Scooty Rent − TDS
function calcFinalAmount(r) {
  return (
    Number(r.amount         || 0) -
    Number(r.penalty        || 0) -
    Number(r.advance        || 0) -
    Number(r.cod_loss       || 0) -
    Number(r.shipment_loss  || 0) -
    Number(r.shipment_loss2 || 0) -
    Number(r.scooty_rent    || 0) -
    Number(r.tds            || 0)
  );
}

const DEDUCTION_FIELDS = ['advance', 'cod_loss', 'shipment_loss', 'shipment_loss2', 'scooty_rent'];

// The most this one deduction field can be set to without pushing Final Amount
// below -1, given the rider's amount/penalty/tds and the OTHER three deduction
// fields' current values. Deductions are entered one field at a time, so this
// is re-checked against whatever is saved in the other three right now.
function maxDeductionValue(r, field) {
  const amount  = Number(r.amount  || 0);
  const penalty = Number(r.penalty || 0);
  const tds     = Number(r.tds     || 0);
  const others = DEDUCTION_FIELDS
    .filter(f => f !== field)
    .reduce((sum, f) => sum + Number(r[f] || 0), 0);
  return amount - penalty - tds - others + 1; // Final Amount floor is -1
}

// Light theme palette
const T = {
  pageBg:      '#ffffff',
  cardBg:      '#f8fafc',
  cardBg2:     '#f1f5f9',
  border:      '#e2e8f0',
  border2:     '#d1d5db',
  headerBg:    '#f1f5f9',
  rowEven:     '#ffffff',
  rowOdd:      '#f8fafc',
  rowExpanded: '#eff6ff',
  textPrimary: '#111827',
  textSecond:  '#374151',
  textMuted:   '#6b7280',
  inputBg:     '#ffffff',
  // Accent — readable on white
  blue:   '#2563eb',
  green:  '#059669',
  amber:  '#d97706',
  red:    '#dc2626',
  purple: '#7c3aed',
  orange: '#ea580c',
};

const inp = {
  background: T.inputBg, color: T.textPrimary, border: `1px solid ${T.border2}`,
  borderRadius: 6, padding: '5px 8px', fontSize: 12,
  fontFamily: "'DM Mono', monospace", outline: 'none', width: '100%',
};

// ── Account Details Modal ─────────────────────────────────────────────────────
function AccountModal({ rider, currentUser, onClose, onSaved }) {
  const [form, setForm] = useState({
    acct_holder: rider.acct_holder || '',
    acct_number: rider.acct_number || '',
    bank_name:   rider.bank_name   || '',
    ifsc_code:   rider.ifsc_code   || '',
    pan_no:      rider.pan_no      || '',
  });
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!form.acct_number.trim() || !form.bank_name.trim() || !form.ifsc_code.trim()) {
      alert('Account Number, Bank Name, and IFSC Code are required.');
      return;
    }
    setSaving(true);
    try {
      await updateRiderAccountDetails(rider.id, form, currentUser?.username || 'hub');
      onSaved({ ...rider, ...form });
    } catch (e) { alert('Failed to save: ' + e.message); }
    setSaving(false);
  }

  // Always readonly for non-admin users — account details are admin-only
  const isReadonly = accountComplete(rider) || (currentUser?.role === 'hub');

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000000cc', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#1a1f2e', border: '1px solid #2a2f3e', borderRadius: 16, width: '100%', maxWidth: 480, fontFamily: "'DM Mono', monospace" }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #2a2f3e', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ color: isReadonly ? T.green : T.orange, fontWeight: 700, fontSize: 13 }}>
              {isReadonly ? '🔒 Account on File' : '✏ Fill Account Details'}
            </div>
            <div style={{ color: T.blue, fontSize: 12, fontWeight: 700, marginTop: 3 }}>{rider.wm_name}</div>
            <div style={{ color: '#6b7280', fontSize: 11, marginTop: 1 }}>{rider.hub_name}</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', color: '#6b7280', border: 'none', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {isReadonly && (
            <div style={{ background: '#d1fae5', border: `1px solid ${T.green}40`, borderRadius: 8, padding: '8px 12px', color: T.green, fontSize: 11 }}>
              Account details are on file. Contact admin if changes are needed.
              {rider.acct_updated_by && <div style={{ color: '#6b7280', marginTop: 3 }}>Last updated by: {rider.acct_updated_by}</div>}
            </div>
          )}
          {[
            { key: 'acct_holder', label: 'ACCOUNT HOLDER NAME' },
            { key: 'acct_number', label: 'ACCOUNT NUMBER *' },
            { key: 'bank_name',   label: 'BANK NAME *' },
            { key: 'ifsc_code',   label: 'IFSC CODE *' },
            { key: 'pan_no',      label: 'PAN NUMBER' },
          ].map(({ key, label }) => (
            <div key={key}>
              <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 4 }}>{label}</label>
              <input
                type="text"
                value={form[key]}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                disabled={isReadonly}
                style={{ ...inp, opacity: isReadonly ? 0.6 : 1, cursor: isReadonly ? 'not-allowed' : 'text' }}
              />
            </div>
          ))}
          {!isReadonly && (
            <button onClick={handleSave} disabled={saving}
              style={{ padding: '10px', background: saving ? '#e5e7eb' : '#f59e0b', color: saving ? '#6b7280' : '#0f1117', border: 'none', borderRadius: 8, fontWeight: 900, cursor: saving ? 'wait' : 'pointer', fontFamily: 'inherit', fontSize: 12, letterSpacing: 1 }}>
              {saving ? 'SAVING...' : '✓ SAVE ACCOUNT DETAILS'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// A tiny inline spinner for "save in progress" states inside table cells.
function MiniSpinner() {
  return (
    <>
      <span style={{
        display: 'inline-block', width: 10, height: 10, marginLeft: 5,
        border: '2px solid #d1d5db', borderTopColor: '#f59e0b',
        borderRadius: '50%', animation: 'wh-mini-spin 0.7s linear infinite',
        verticalAlign: 'middle', flexShrink: 0,
      }} />
      <style>{`@keyframes wh-mini-spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}

// ── Labeled multi-select filter (checkbox popover) ────────────────────────────
// selected = [] means "All" (no filter applied). Click the field name's own
// checkbox list to pick one or more values at once.
function MultiSelectFilter({ label, options, selected, onChange, color, inputStyle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const allSelected = selected.length === 0;
  const summary = allSelected
    ? 'All'
    : (selected.length === 1 ? selected[0] : `${selected.length} selected`);

  function toggle(opt) {
    onChange(selected.includes(opt) ? selected.filter(s => s !== opt) : [...selected, opt]);
  }

  return (
    <div ref={ref} style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 9, color: '#9ca3af', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>{label}</span>
      <button type="button" onClick={() => setOpen(v => !v)}
        style={{ ...inputStyle, cursor: 'pointer', textAlign: 'left', minWidth: 110,
          fontWeight: !allSelected ? 700 : 400, color: !allSelected ? (color || inputStyle.color) : inputStyle.color }}>
        {summary} ▾
      </button>
      {open && (
        // Popover background is a fixed dark color regardless of app theme, so its
        // text is hardcoded light too — using the theme's own text color here made
        // labels unreadable (dark-on-dark) whenever the app was in light mode.
        <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 50, marginTop: 2,
          background: '#1f2937', border: '1px solid #4b5563', borderRadius: 8, padding: 6,
          minWidth: 170, maxHeight: 260, overflowY: 'auto', boxShadow: '0 4px 16px rgba(0,0,0,0.45)' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px', fontSize: 12, cursor: 'pointer', fontWeight: 700, color: '#f9fafb' }}>
            <input type="checkbox" checked={allSelected} onChange={() => onChange([])} />
            All
          </label>
          <div style={{ borderTop: '1px solid #4b5563', margin: '4px 0' }} />
          {options.map(opt => (
            <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px', fontSize: 12, cursor: 'pointer', color: '#e5e7eb' }}>
              <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} />
              {opt}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Inline editable numeric cell ──────────────────────────────────────────────
function EditCell({ value, onSave, color = '#f3f4f6', readOnly = false, maxValue = null }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [pendingValue, setPendingValue] = useState(null);
  const ref = useRef();

  function startEdit() {
    if (readOnly) return;
    setDraft(value != null ? String(value) : '');
    setEditing(true);
    setTimeout(() => ref.current?.focus(), 0);
  }

  async function commit() {
    const num = draft.trim() === '' ? null : Number(draft);
    const finalNum = isNaN(num) ? null : num;
    if (finalNum === value) { setEditing(false); return; }
    // Deduction fields can't push Final Amount below -1 — reject and let the
    // user correct the number instead of silently saving a negative payout.
    // (Clearing a value to blank has no such cap, so it's exempt from this check.)
    if (finalNum !== null && maxValue != null && finalNum > maxValue) {
      alert(maxValue >= 0
        ? `This would make the Final Amount go below -1. Maximum allowed here right now is ${fmt(Math.floor(maxValue))}.`
        : `Final Amount is already at or below -1 from other deductions — this field can't be increased further.`);
      return; // stay in edit mode so they can fix the number
    }
    setEditing(false);
    // Show the new value right away with a spinner instead of flashing back to
    // the old one while the save round-trips — this was confusing people into
    // thinking the entry hadn't registered.
    setPendingValue(finalNum);
    setSaving(true);
    try {
      await onSave(finalNum);
    } finally {
      setSaving(false);
      setPendingValue(null);
    }
  }

  if (editing) return (
    <input
      ref={ref}
      type="number"
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
      style={{ ...inp, width: 90, padding: '3px 6px', fontSize: 12 }}
    />
  );

  const displayValue = saving ? pendingValue : value;
  return (
    <span
      onClick={saving ? undefined : startEdit}
      title={readOnly ? undefined : (saving ? 'Saving…' : 'Click to edit')}
      style={{
        color: displayValue != null ? color : '#9ca3af',
        cursor: readOnly ? 'default' : (saving ? 'wait' : 'cell'),
        opacity: saving ? 0.6 : 1,
        display: 'flex', alignItems: 'center',
        minWidth: 60,
        padding: '2px 0',
        borderBottom: readOnly ? 'none' : '1px dashed #d1d5db',
      }}>
      {displayValue != null ? fmt(displayValue) : '–'}
      {saving && <MiniSpinner />}
    </span>
  );
}

// ── Inline editable text cell ─────────────────────────────────────────────────
function TextCell({ value, onSave, color = '#9ca3af', readOnly = false, options }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [pendingValue, setPendingValue] = useState(undefined); // undefined = not pending
  const ref = useRef();

  async function saveOptimistically(v) {
    setEditing(false);
    setPendingValue(v);
    setSaving(true);
    try {
      await onSave(v);
    } finally {
      setSaving(false);
      setPendingValue(undefined);
    }
  }

  function startEdit() {
    if (readOnly) return;
    setDraft(value || '');
    setEditing(true);
    setTimeout(() => ref.current?.focus(), 0);
  }

  async function commit() {
    const v = draft.trim() || null;
    if (v === (value || null)) { setEditing(false); return; }
    if (v === null && value) {
      if (!window.confirm(`Clear this value (currently "${value}")? This cannot be undone.`)) return;
    }
    await saveOptimistically(v);
  }

  if (editing) {
    if (options) return (
      <select
        ref={ref}
        value={draft}
        onChange={e => { setDraft(e.target.value); }}
        onBlur={async () => {
          const v = draft || null;
          if (v === (value || null)) { setEditing(false); return; }
          if (v === null && value) {
            if (!window.confirm(`Clear this value (currently "${value}")? This cannot be undone.`)) return;
          }
          await saveOptimistically(v);
        }}
        style={{ ...inp, width: 110, padding: '3px 6px', fontSize: 11 }}>
        <option value="">–</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
    return (
      <input
        ref={ref}
        type="text"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
        style={{ ...inp, width: 130, padding: '3px 6px', fontSize: 11 }}
      />
    );
  }

  const displayValue = saving ? pendingValue : value;
  return (
    <span
      onClick={saving ? undefined : startEdit}
      title={readOnly ? undefined : (saving ? 'Saving…' : 'Click to edit')}
      style={{
        color: displayValue ? color : '#9ca3af',
        cursor: readOnly ? 'default' : (saving ? 'wait' : 'cell'),
        opacity: saving ? 0.6 : 1,
        display: 'flex', alignItems: 'center',
        maxWidth: 140,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        borderBottom: readOnly ? 'none' : '1px dashed #d1d5db',
        padding: '2px 0',
        fontSize: 11,
      }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayValue || '–'}</span>
      {saving && <MiniSpinner />}
    </span>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
/** Extract Google Sheet ID from a full URL or return the string as-is if it looks like an ID already */
function extractSheetId(urlOrId) {
  if (!urlOrId) return null;
  const m = urlOrId.match(/\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/);
  if (m) return m[1];
  // If no URL pattern found, treat the whole string as an ID
  if (/^[a-zA-Z0-9_-]{20,}$/.test(urlOrId.trim())) return urlOrId.trim();
  return null;
}

// ── Upload Modal (Admin only) ─────────────────────────────────────────────────
function UploadModal({ onClose, onUploaded }) {
  const [source,  setSource]  = useState('sheets'); // 'sheets' | 'excel'
  // Google Sheets fields
  const [sheetUrl, setSheetUrl] = useState('');
  const [period,   setPeriod]   = useState('');
  // Excel fields
  const [file,     setFile]     = useState(null);
  const [exPeriod, setExPeriod] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [status,   setStatus]   = useState(''); // progress message
  const fileRef = useRef();

  // ── Google Sheets fetch ──────────────────────────────────────────────────────
  async function handleSheetsFetch() {
    const sheetId = extractSheetId(sheetUrl.trim());
    if (!sheetId) { alert('Paste a valid Google Sheets URL or Sheet ID.'); return; }
    if (!period.trim()) { alert('Enter a period label, e.g. "Jun-26".'); return; }
    setLoading(true);
    setStatus('Connecting to Google Sheets…');
    try {
      const { data, error } = await supabase.functions.invoke('fetch-payment-sheet', {
        body: { sheetId, tabName: 'ODH' },
      });
      if (error) throw new Error(error.message || 'Edge function error');
      if (data?.error) throw new Error(data.error);
      const rawRows = data?.rows || [];
      if (rawRows.length === 0) throw new Error('The ODH tab returned no rows. Check that the tab name is exactly "ODH" and the sheet has data.');
      setStatus(`Parsing ${rawRows.length} rows…`);
      const riders = parsePaymentSheetRows(rawRows);
      if (riders.length === 0) throw new Error('No valid rider rows found — make sure WM Name and Hub Name columns are present.');
      setStatus(`Saving ${riders.length} riders…`);
      const upload = await createPaymentUpload(`Google Sheets — ODH (${period.trim()})`, period.trim(), sheetId);
      await insertPaymentRiders(upload.id, riders);
      onUploaded(upload, riders.length);
    } catch (e) {
      alert('Fetch failed: ' + e.message);
    }
    setLoading(false);
    setStatus('');
  }

  // ── Excel upload ─────────────────────────────────────────────────────────────
  async function handleExcelUpload() {
    if (!file || !exPeriod.trim()) { alert('Select a file and enter a period (e.g. "Jun-26").'); return; }
    setLoading(true);
    setStatus('Parsing file…');
    try {
      const buffer = await file.arrayBuffer();
      const riders = parsePaymentSheet(buffer);
      if (riders.length === 0) throw new Error('No valid rider rows found in the ODH tab.');
      setStatus(`Saving ${riders.length} riders…`);
      const upload = await createPaymentUpload(file.name, exPeriod.trim());
      await insertPaymentRiders(upload.id, riders);
      onUploaded(upload, riders.length);
    } catch (e) { alert('Upload failed: ' + e.message); }
    setLoading(false);
    setStatus('');
  }

  const tabBtn = (key, label) => (
    <button key={key} onClick={() => setSource(key)}
      style={{
        flex: 1, padding: '9px 0', border: 'none', borderRadius: 8, cursor: 'pointer',
        fontFamily: 'inherit', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, transition: 'all .15s',
        background: source === key ? (key === 'sheets' ? '#dbeafe' : '#fef3c7') : 'transparent',
        color: source === key ? (key === 'sheets' ? T.blue : T.amber) : '#9ca3af',
      }}>
      {label}
    </button>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000000cc', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#1a1f2e', border: '1px solid #2a2f3e', borderRadius: 16, width: '100%', maxWidth: 480, fontFamily: "'DM Mono', monospace" }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #2a2f3e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ color: '#f59e0b', fontWeight: 700, fontSize: 13 }}>📥 Import Payment Sheet (ODH)</div>
          <button onClick={onClose} style={{ background: 'transparent', color: '#6b7280', border: 'none', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        {/* Source switcher */}
        <div style={{ display: 'flex', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 10, margin: '16px 20px 0', padding: 3, gap: 2 }}>
          {tabBtn('sheets', '📊 Google Sheets')}
          {tabBtn('excel',  '📁 Upload Excel')}
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* ── Google Sheets pane ── */}
          {source === 'sheets' && (<>
            <div style={{ background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: 8, padding: '10px 14px', fontSize: 11, color: T.blue, lineHeight: 1.7 }}>
              Share the sheet with your service account email (Viewer access), then paste the URL below.
            </div>
            <div>
              <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 6 }}>GOOGLE SHEETS URL</label>
              <input type="text" placeholder="https://docs.google.com/spreadsheets/d/…"
                value={sheetUrl} onChange={e => setSheetUrl(e.target.value)} style={inp} />
            </div>
            <div>
              <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 6 }}>PERIOD LABEL (e.g. Jun-26)</label>
              <input type="text" placeholder="Jun-26" value={period} onChange={e => setPeriod(e.target.value)} style={inp} />
            </div>
            <button onClick={handleSheetsFetch} disabled={loading}
              style={{ padding: '11px', background: loading ? '#e5e7eb' : '#dbeafe', color: loading ? '#9ca3af' : T.blue, border: `1px solid ${loading ? '#d1d5db' : '#93c5fd'}`, borderRadius: 8, fontWeight: 900, cursor: loading ? 'wait' : 'pointer', fontFamily: 'inherit', fontSize: 12, letterSpacing: 2 }}>
              {loading ? (status || 'FETCHING…') : '↓ FETCH FROM GOOGLE SHEETS'}
            </button>
          </>)}

          {/* ── Excel pane ── */}
          {source === 'excel' && (<>
            <div>
              <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 6 }}>PERIOD LABEL (e.g. Jun-26)</label>
              <input type="text" placeholder="Jun-26" value={exPeriod} onChange={e => setExPeriod(e.target.value)} style={inp} />
            </div>
            <div>
              <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 6 }}>EXCEL FILE (ODH tab required)</label>
              <div onClick={() => fileRef.current?.click()}
                style={{ background: '#f8fafc', border: `2px dashed ${file ? T.green : '#d1d5db'}`, borderRadius: 8, padding: 16, textAlign: 'center', cursor: 'pointer' }}>
                {file
                  ? <div style={{ color: T.green, fontSize: 12 }}>✓ {file.name}</div>
                  : <div style={{ color: '#6b7280', fontSize: 12 }}>📎 Click to select Excel file</div>}
              </div>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={e => setFile(e.target.files[0])} style={{ display: 'none' }} />
            </div>
            <button onClick={handleExcelUpload} disabled={loading}
              style={{ padding: '11px', background: loading ? '#e5e7eb' : '#f59e0b', color: loading ? '#9ca3af' : '#0f1117', border: 'none', borderRadius: 8, fontWeight: 900, cursor: loading ? 'wait' : 'pointer', fontFamily: 'inherit', fontSize: 12, letterSpacing: 2 }}>
              {loading ? (status || 'UPLOADING…') : 'UPLOAD →'}
            </button>
          </>)}

          <div style={{ color: T.textMuted, fontSize: 10, textAlign: 'center' }}>
            All uploads are preserved — switch between periods via the dropdown.
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main PaymentSheet Component ───────────────────────────────────────────────
/**
 * Props:
 *   user       — hub user / manager object (has .role, .hub_name, .managed_hubs, .username)
 *   isAdmin    — boolean — true for both full Admin and Control Tower (admin-level dashboard access)
 *   fullAdmin  — boolean — true only for real PIN-based Admin. Control Tower gets isAdmin=true but
 *                fullAdmin=false, which hides Excel export and Delete Upload. Lock All / Unlock All
 *                is isAdmin-gated (both full Admin and Control Tower can bulk lock/unlock).
 */
export default function PaymentSheet({ user, isAdmin, fullAdmin = isAdmin }) {
  const [uploads,       setUploads]       = useState([]);
  const [selectedId,    setSelectedId]    = useState(null);
  const [riders,        setRiders]        = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [showUpload,    setShowUpload]     = useState(false);
  const [syncing,       setSyncing]       = useState(false);
  const [deduping,      setDeduping]      = useState(false);
  const [backfilling,   setBackfilling]   = useState(false);
  const [backfillProgress, setBackfillProgress] = useState(null); // { done, total }
  const [bulkLocking,   setBulkLocking]   = useState(false); // bulk lock/unlock in progress
  const [showLog,       setShowLog]       = useState(false);
  const [auditLog,      setAuditLog]      = useState([]);
  const [logLoading,    setLogLoading]    = useState(false);
  const [logSearch,     setLogSearch]     = useState('');
  const [accountModal,  setAccountModal]  = useState(null); // rider object
  const [search,        setSearch]        = useState('');
  const [filterActive,   setFilterActive]   = useState([]);  // [] = All, else array of 'Active'/'Inactive'
  const [filterMissing,  setFilterMissing]  = useState(false);  // only missing account details
  const [filterHub,      setFilterHub]      = useState([]);  // [] = All, else array of hub names
  const [filterState,    setFilterState]    = useState([]);  // [] = All, else array of states
  const [filterDecision, setFilterDecision] = useState([]); // [] = All, else array of pmt_decision values
  const [filterPmtStatus,setFilterPmtStatus]= useState([]); // [] = All, else array of pmt_status values
  const [expandedId,    setExpandedId]    = useState(null);   // row expanded for details

  // Hub scope for this user
  const hubScope = useMemo(() => {
    if (isAdmin) return null; // null = no filter (see all)
    if (user?.role === 'manager') return user.managed_hubs || [];
    if (user?.hub_name) return [user.hub_name];
    return [];
  }, [isAdmin, user]);

  // Load upload list on mount
  useEffect(() => {
    getPaymentUploads().then(list => {
      setUploads(list);
      if (list.length > 0) setSelectedId(list[0].id);
    }).catch(console.error);
  }, []);

  // Load riders whenever selected upload changes
  useEffect(() => {
    if (!selectedId) return;
    setLoading(true);
    setRiders([]);
    getPaymentRiders(selectedId, hubScope, !isAdmin)
      .then(data => setRiders(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedId]); // eslint-disable-line

  // Realtime: pick up sync / lock / unlock done by someone ELSE with this same
  // upload open, instead of leaving them on a stale snapshot until they manually
  // refresh. This matters more than ordinary cosmetic staleness: a sync rewrites
  // rider data from the sheet, and a lock changes who's allowed to edit a row —
  // so two people can end up editing the same row, or editing a row someone just
  // locked, during the window before a manual refresh. Uses a Supabase Realtime
  // broadcast channel scoped to the selected upload (not a Postgres change feed —
  // a sync/bulk-lock can touch hundreds of rows at once, and we only need "go
  // re-fetch", not a stream of per-row diffs).
  const refreshChannelRef = useRef(null);
  const noticeTimerRef    = useRef(null);
  const [refreshNotice, setRefreshNotice] = useState(null); // { reason, by } | null

  useEffect(() => {
    if (!selectedId) return;
    const channel = supabase
      .channel(`payment-sheet-${selectedId}`, { config: { broadcast: { self: false } } })
      .on('broadcast', { event: 'refresh' }, ({ payload }) => {
        getPaymentRiders(selectedId, hubScope, !isAdmin)
          .then(data => setRiders(data))
          .catch(console.error);
        setRefreshNotice(payload);
        clearTimeout(noticeTimerRef.current);
        noticeTimerRef.current = setTimeout(() => setRefreshNotice(null), 6000);
      })
      .subscribe();
    refreshChannelRef.current = channel;
    return () => {
      clearTimeout(noticeTimerRef.current);
      supabase.removeChannel(channel);
      refreshChannelRef.current = null;
    };
  }, [selectedId]); // eslint-disable-line

  // Tell every other open tab/browser on this upload to re-fetch riders right now.
  function broadcastRefresh(reason) {
    refreshChannelRef.current?.send({
      type: 'broadcast', event: 'refresh',
      payload: { reason, by: actorName, at: new Date().toISOString() },
    });
  }

  // Unique hubs in this data (for hub filter dropdown — admin/manager only)
  const hubOptions = useMemo(() =>
    ['All', ...[...new Set(riders.map(r => r.hub_name).filter(Boolean))].sort()],
    [riders]
  );

  // Unique states for state filter
  const stateOptions = useMemo(() =>
    ['All', ...[...new Set(riders.map(r => r.state).filter(Boolean))].sort()],
    [riders]
  );

  // Unique pmt_decision values
  const decisionOptions = useMemo(() =>
    ['All', ...[...new Set(riders.map(r => r.pmt_decision).filter(Boolean))].sort()],
    [riders]
  );

  // Unique pmt_status values (normalized so "Done 30/07" groups under a "Done*" bucket)
  const pmtStatusOptions = useMemo(() =>
    ['All', ...[...new Set(riders.map(r => r.pmt_status).filter(Boolean))].sort()],
    [riders]
  );

  // Filtered + sorted riders: State → Hub Name → Rider Name
  const filtered = useMemo(() => {
    const result = riders.filter(r => {
      // Hide "Hold" rows from non-admin users (ground team / hub team)
      if (!isAdmin && r.pmt_hold && r.pmt_hold.toLowerCase() === 'hold') return false;
      if (search && !r.wm_name?.toLowerCase().includes(search.toLowerCase()) && !r.hub_name?.toLowerCase().includes(search.toLowerCase())) return false;
      if (filterActive.length && !filterActive.includes(r.currently_active)) return false;
      if (filterMissing && accountComplete(r)) return false;
      if (filterHub.length && !filterHub.includes(r.hub_name)) return false;
      if (filterState.length && !filterState.includes(r.state)) return false;
      if (filterDecision.length && !filterDecision.includes(r.pmt_decision)) return false;
      if (filterPmtStatus.length && !filterPmtStatus.includes(r.pmt_status)) return false;
      return true;
    });
    result.sort((a, b) => {
      const s = (a.state || '').localeCompare(b.state || '');
      if (s !== 0) return s;
      const h = (a.hub_name || '').localeCompare(b.hub_name || '');
      if (h !== 0) return h;
      return (a.wm_name || '').localeCompare(b.wm_name || '');
    });
    return result;
  }, [riders, search, filterActive, filterMissing, filterHub, filterState, filterDecision, filterPmtStatus, isAdmin]);

  // Helper: treat "Done", "Done 30/07", "Done 2026-07-30", "paid", etc. as paid
  const isPaidStatus = r => !!(r.pmt_status &&
    (r.pmt_status.toLowerCase().startsWith('done') || r.pmt_status.toLowerCase() === 'paid'));

  // Stats
  const stats = useMemo(() => {
    const total           = filtered.length;
    const missing         = filtered.filter(r => !accountComplete(r)).length;
    const totalAmt        = filtered.reduce((s, r) => s + Number(r.amount || 0), 0);
    const totalFinal      = filtered.reduce((s, r) => s + calcFinalAmount(r), 0);
    const paidAmt         = filtered.filter(isPaidStatus).reduce((s, r) => s + calcFinalAmount(r), 0);
    const pendingAmt      = filtered.filter(r => !isPaidStatus(r)).reduce((s, r) => s + calcFinalAmount(r), 0);
    const totalAttempted  = filtered.reduce((s, r) => s + Number(r.total_attempted || 0), 0);
    const totalDelivered  = filtered.reduce((s, r) => s + Number(r.odh_delivered   || 0), 0);
    const conversion      = totalAttempted > 0 ? (totalDelivered / totalAttempted) * 100 : null;
    const cps             = totalDelivered > 0 ? totalAmt / totalDelivered : null;
    const totalAdvance    = filtered.reduce((s, r) => s + Number(r.advance      || 0), 0);
    const totalCodLoss    = filtered.reduce((s, r) => s + Number(r.cod_loss     || 0), 0);
    const totalShipLoss   = filtered.reduce((s, r) => s + Number(r.shipment_loss|| 0), 0);
    return { total, missing, totalAmt, totalFinal, paidAmt, pendingAmt, totalAttempted, totalDelivered, conversion, cps, totalAdvance, totalCodLoss, totalShipLoss };
  }, [filtered]); // eslint-disable-line

  // Patch a rider locally after save
  function patchRider(id, fields) {
    setRiders(prev => prev.map(r => r.id === id ? { ...r, ...fields } : r));
  }

  // Bulk lock or unlock all eligible filtered rows
  async function handleBulkLock(lockValue) {
    // Eligible: not done-locked, and (if unlocking) must be manually locked
    const eligible = filtered.filter(r => {
      if (isPaidStatus(r)) return false; // never touch done-locked rows
      if (lockValue) return !r.is_locked; // lock: only unlocked rows
      return !!r.is_locked;               // unlock: only manually locked rows
    });
    if (eligible.length === 0) {
      alert(lockValue ? 'No unlocked rows to lock in current filter.' : 'No manually locked rows to unlock in current filter.');
      return;
    }
    const action = lockValue ? 'lock' : 'unlock';
    if (!window.confirm(`${lockValue ? '🔒 Lock' : '🔓 Unlock'} ${eligible.length} row${eligible.length > 1 ? 's' : ''}?`)) return;
    setBulkLocking(true);
    try {
      await Promise.all(eligible.map(r => updatePaymentRiderLock(r.id, lockValue)));
      setRiders(prev => prev.map(r => {
        if (eligible.some(e => e.id === r.id)) return { ...r, is_locked: lockValue };
        return r;
      }));
      logPaymentAction({
        uploadId: selectedId, riderId: null,
        riderName: `${eligible.length} riders (bulk)`, hubName: null,
        action: `bulk_${action}`, fieldName: 'is_locked',
        oldValue: String(!lockValue), newValue: String(lockValue), doneBy: actorName,
      });
      broadcastRefresh(lockValue ? 'lock_all' : 'unlock_all');
    } catch (err) {
      alert(`Bulk ${action} failed: ${err.message}`);
    }
    setBulkLocking(false);
  }

  // Username of whoever is currently acting
  const actorName = user?.username || (isAdmin ? 'admin' : 'unknown');

  // Identity key for duplicate detection.
  // IMPORTANT: a Casper Employee ID is NOT unique per row — the same rider can
  // legitimately have two rows in one upload if they worked out of two
  // different hubs in that period (each hub gets its own payout line with its
  // own numbers). So "same Casper ID" alone does NOT mean "duplicate row" — it
  // must also be the same hub. Casper ID + Hub is a real duplicate: nothing
  // legitimate explains the same employee having two rows for the same hub in
  // the same upload, so that match is safe to act on automatically. Name+hub
  // with no Casper ID on either row is weaker still (two different real people
  // can share a common name at the same hub) and is only ever surfaced for
  // manual review, never auto-deleted.
  function riderIdentityKey(r) {
    const casper = String(r.casper_employee_id || '').trim();
    const hub    = String(r.hub_name || '').trim().toUpperCase();
    if (casper && hub) return { key: `C::${casper}::${hub}`, type: 'casper' };
    const name = String(r.wm_name || '').trim().toUpperCase();
    return (name && hub) ? { key: `N::${name}::${hub}`, type: 'namehub' } : null;
  }

  // Duplicate rider rows within the currently selected upload. Split into:
  //  - casperGroups: same Casper Employee ID more than once — definitely the
  //    same person, safe to auto-clean.
  //  - nameHubGroups: same name+hub more than once with no Casper ID on either
  //    row — *possibly* the same person duplicated, but possibly two different
  //    people with the same name — needs a human to check, never auto-deleted.
  // Computed over the full, unfiltered rider list for this upload, not just
  // what's currently visible.
  const { casperGroups, nameHubGroups } = useMemo(() => {
    const byKey = {};
    riders.forEach(r => {
      const id = riderIdentityKey(r);
      if (!id) return;
      (byKey[id.key] = byKey[id.key] || { type: id.type, rows: [] }).rows.push(r);
    });
    const groups = Object.values(byKey).filter(g => g.rows.length > 1);
    return {
      casperGroups:  groups.filter(g => g.type === 'casper').map(g => g.rows),
      nameHubGroups: groups.filter(g => g.type === 'namehub').map(g => g.rows),
    };
  }, [riders]);
  const duplicateExtraCount = casperGroups.reduce((sum, g) => sum + (g.length - 1), 0);
  const nameHubExtraCount   = nameHubGroups.reduce((sum, g) => sum + (g.length - 1), 0);

  // Remove duplicate rows (Casper-ID-matched groups only — see note above),
  // keeping the most recently updated row in each group (it's the one most
  // likely to hold the latest correct values) and deleting the rest. Logs one
  // audit entry per row removed so the cleanup is traceable.
  async function handleRemoveDuplicates() {
    if (casperGroups.length === 0) return;
    const reviewNote = nameHubExtraCount > 0
      ? `\n\n(${nameHubExtraCount} more row${nameHubExtraCount > 1 ? 's' : ''} share a name+hub with no Casper ID on file — ` +
        `those are NOT included here since they might be different people. Use "Review possible duplicates" for those.)`
      : '';
    const ok = window.confirm(
      `Found ${duplicateExtraCount} duplicate rider row${duplicateExtraCount > 1 ? 's' : ''} ` +
      `(same Casper Employee ID) across ${casperGroups.length} rider${casperGroups.length > 1 ? 's' : ''} in this upload.\n\n` +
      `For each one, the most recently updated row will be kept and the other copy/copies ` +
      `will be permanently deleted. This cannot be undone.${reviewNote}\n\nContinue?`
    );
    if (!ok) return;
    setDeduping(true);
    try {
      const idsToDelete = [];
      casperGroups.forEach(group => {
        const sorted = [...group].sort((a, b) =>
          new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
        const [, ...extras] = sorted; // keep sorted[0], delete the rest
        extras.forEach(r => {
          idsToDelete.push(r.id);
          logPaymentAction({
            uploadId: selectedId, riderId: r.id,
            riderName: r.wm_name, hubName: r.hub_name, casperId: r.casper_employee_id,
            action: 'duplicate_removed', fieldName: null,
            oldValue: `kept row ${sorted[0].id}`, newValue: null, doneBy: actorName,
          });
        });
      });
      await deletePaymentRiders(idsToDelete);
      setRiders(prev => prev.filter(r => !idsToDelete.includes(r.id)));
      alert(`✓ Removed ${idsToDelete.length} duplicate row${idsToDelete.length > 1 ? 's' : ''}.`);
    } catch (e) {
      alert('Duplicate removal failed: ' + e.message);
    }
    setDeduping(false);
  }

  // Name+hub-only matches are never auto-deleted (see note above) — this just
  // lists them so an admin can eyeball each pair/group and decide for
  // themselves, e.g. by checking Casper IDs in the source sheet.
  function handleReviewNameHubDuplicates() {
    if (nameHubGroups.length === 0) return;
    const lines = nameHubGroups.map(group =>
      `• ${group[0].wm_name} (${group[0].hub_name}) — ${group.length} rows, row ids: ${group.map(r => r.id.slice(0, 8)).join(', ')}`
    );
    console.log('Possible duplicate riders (name+hub match, no Casper ID):\n' + lines.join('\n'));
    alert(
      `${nameHubGroups.length} rider name${nameHubGroups.length > 1 ? 's' : ''} share a name+hub with no Casper ID on ` +
      `either row, so they were NOT auto-deleted — they might be two different people.\n\n` +
      lines.slice(0, 25).join('\n') +
      (lines.length > 25 ? `\n…and ${lines.length - 25} more (full list printed to the browser console).` : '') +
      `\n\nCheck these against the source sheet / their Casper IDs before deleting anything manually.`
    );
  }

  // Fire-and-forget write-back to the source Google Sheet.
  // Silently skipped when the upload came from Excel (sheet_id = null)
  // or when the rider has no sheet_row_index stored.
  function writeBack(rider, fields) {
    if (!selectedUpload?.sheet_id || !rider?.sheet_row_index) return;
    supabase.functions.invoke('update-payment-sheet', {
      body: {
        sheetId:          selectedUpload.sheet_id,
        tabName:          'ODH',
        // rowIndex is only a fallback now — the edge function re-locates the row by
        // Casper Employee ID + Hub Name first, so a sheet reorder between syncs
        // can't cause a write to land on the wrong rider's row. Hub Name is
        // required alongside Casper ID because the same rider can have two rows
        // (one per hub) in the same period — Casper ID alone isn't unique
        // (see the 2026-10-16 "Advance applied to the wrong hub" incident).
        rowIndex:         rider.sheet_row_index,
        casperEmployeeId: rider.casper_employee_id || undefined,
        hubName:          rider.hub_name || undefined,
        fields,
      },
    }).catch(err => console.warn('Sheet write-back failed (non-fatal):', err));
  }

  async function handleFieldSave(riderId, field, value) {
    try {
      const rider = riders.find(r => r.id === riderId);
      await updatePaymentRiderFields(riderId, { [field]: value });
      logPaymentAction({
        uploadId: selectedId, riderId, riderName: rider?.wm_name, hubName: rider?.hub_name,
        casperId: rider?.casper_employee_id,
        action: 'field_edit', fieldName: field,
        oldValue: rider?.[field], newValue: value, doneBy: actorName,
      });
      patchRider(riderId, { [field]: value });
      writeBack(rider, { [field]: value });
    } catch (e) { alert('Save failed: ' + e.message); }
  }

  async function handleStatusSave(riderId, value) {
    if (!isAdmin) return;
    try {
      await updatePaymentRiderStatus(riderId, value);
      patchRider(riderId, { pmt_status: value });
      const rider = riders.find(r => r.id === riderId);
      writeBack(rider, { pmt_status: value });
    } catch (e) { alert('Save failed: ' + e.message); }
  }

  async function handleDeleteUpload(uploadId) {
    if (!window.confirm('Delete this payment upload and all its rider data? This cannot be undone.')) return;
    try {
      await deletePaymentUpload(uploadId);
      const newList = uploads.filter(u => u.id !== uploadId);
      setUploads(newList);
      setSelectedId(newList.length > 0 ? newList[0].id : null);
      setRiders([]);
    } catch (e) { alert('Delete failed: ' + e.message); }
  }

  async function handleSync() {
    if (!selectedUpload?.sheet_id) return;
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('fetch-payment-sheet', {
        body: { sheetId: selectedUpload.sheet_id, tabName: 'ODH' },
      });
      if (error) throw new Error(error.message || 'Edge function error');
      if (data?.error) throw new Error(data.error);
      const rawRows = data?.rows || [];
      if (rawRows.length === 0) throw new Error('Sheet returned no rows.');
      const riders = parsePaymentSheetRows(rawRows);
      if (riders.length === 0) throw new Error('No valid rider rows found in the sheet.');
      await syncPaymentRidersFromSheet(selectedUpload.id, riders);
      // Reload riders from DB to reflect changes
      const fresh = await getPaymentRiders(selectedUpload.id, hubScope);
      setRiders(fresh);
      broadcastRefresh('sync'); // tell everyone else with this upload open to re-fetch too
      // Record + reflect when this sync happened, so "Last synced" stays accurate
      // without needing to re-fetch the whole uploads list. Non-fatal: if this
      // fails (e.g. the last_synced_at column hasn't been migrated in yet) the
      // sync itself already succeeded above, so don't surface it as a failure.
      try {
        const syncedAt = await markUploadSynced(selectedUpload.id);
        setUploads(prev => prev.map(u => u.id === selectedUpload.id ? { ...u, last_synced_at: syncedAt } : u));
      } catch (e) {
        console.warn('Could not record last_synced_at (non-fatal):', e.message);
      }
      alert(`✓ Synced ${riders.length} riders from Google Sheets.`);
    } catch (e) {
      alert('Sync failed: ' + e.message);
    }
    setSyncing(false);
  }

  // One-off: push every rider's currently-set app-owned fields (Pmt Decision,
  // Remarks, deductions, account details, Pmt Status) back to the Google Sheet.
  // For use after write-back has been broken for a while — e.g. the 2026-09-28
  // incident where a protected range on the sheet silently rejected every write —
  // so values already saved in the app get pushed to the sheet once the underlying
  // problem is fixed, instead of waiting for someone to re-enter each one by hand.
  async function handleBackfillToSheet() {
    if (!selectedUpload?.sheet_id) return;
    const candidateCount = riders.filter(r =>
      r.sheet_row_index && ['advance','cod_loss','shipment_loss','scooty_rent','pmt_decision','remarks','pmt_status','acct_holder','acct_number','bank_name','ifsc_code','pan_no']
        .some(f => r[f] != null && r[f] !== '')
    ).length;
    if (candidateCount === 0) {
      alert('No riders with saved values to push — nothing to backfill.');
      return;
    }
    if (!window.confirm(`This will push saved values (Pmt Decision, Remarks, deductions, account details, Pmt Status) for up to ${candidateCount} rider(s) into the Google Sheet, overwriting whatever is currently in those cells. Continue?`)) return;
    setBackfilling(true);
    setBackfillProgress({ done: 0, total: candidateCount });
    try {
      const result = await backfillRidersToSheet(selectedUpload, riders, (done, total) => setBackfillProgress({ done, total }));
      if (result.failed.length === 0) {
        alert(`✓ Backfilled ${result.pushed} rider(s) to the Google Sheet.`);
      } else {
        const preview = result.failed.slice(0, 10).map(f => `• ${f.rider} (${f.hub}): ${f.error}`).join('\n');
        alert(`Backfilled ${result.pushed} of ${result.total} rider(s).\n${result.failed.length} failed:\n${preview}${result.failed.length > 10 ? `\n…and ${result.failed.length - 10} more` : ''}`);
      }
    } catch (e) {
      alert('Backfill failed: ' + e.message);
    }
    setBackfilling(false);
    setBackfillProgress(null);
  }

  const inputStyle = {
    background: T.inputBg, color: T.textPrimary, border: `1px solid ${T.border2}`,
    borderRadius: 8, padding: '7px 12px', fontSize: 12, fontFamily: "'DM Mono', monospace", outline: 'none',
  };

  const selectedUpload = uploads.find(u => u.id === selectedId);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '8px 16px 16px', display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 1800, margin: '0 auto', fontFamily: "'DM Mono', monospace", background: T.pageBg, minHeight: '100vh' }}>

      {/* ── Audit Log Modal (admin only) ──────────────────────────────────── */}
      {isAdmin && showLog && (() => {
        const logQ = logSearch.trim().toLowerCase();
        const visibleLog = logQ
          ? auditLog.filter(e =>
              [e.done_by, e.hub_name, e.rider_name, e.casper_employee_id, e.action, e.field_name, e.old_value, e.new_value]
                .some(v => v && String(v).toLowerCase().includes(logQ))
            )
          : auditLog;
        return (
          <div style={{ position: 'fixed', inset: 0, background: '#000000cc', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
            onClick={e => { if (e.target === e.currentTarget) { setShowLog(false); setLogSearch(''); } }}>
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 16, width: '100%', maxWidth: 1000, maxHeight: '85vh', display: 'flex', flexDirection: 'column', fontFamily: "'DM Mono', monospace" }}>
              {/* Header */}
              <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div>
                    <div style={{ color: T.amber, fontWeight: 700, fontSize: 13 }}>📋 Audit Log</div>
                    <div style={{ color: T.textMuted, fontSize: 10, marginTop: 2 }}>
                      {logQ ? `${visibleLog.length} of ${auditLog.length}` : auditLog.length} entries · newest first
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    {auditLog.length > 0 && (
                      <button
                        onClick={() => exportAuditLogToExcel(auditLog, `Wheeley_AuditLog_${new Date().toISOString().split('T')[0]}.xlsx`)}
                        style={{ background: '#d1fae5', color: T.green, border: `1px solid ${T.green}`, borderRadius: 8, padding: '6px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                        ⬇ Download
                      </button>
                    )}
                    <button onClick={() => { setShowLog(false); setLogSearch(''); }} style={{ background: 'transparent', color: '#6b7280', border: 'none', fontSize: 20, cursor: 'pointer' }}>✕</button>
                  </div>
                </div>
                {/* Search bar */}
                <input
                  type="text"
                  placeholder="Search by rider, FHRID, hub, action, user, field, value…"
                  value={logSearch}
                  onChange={e => setLogSearch(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', background: T.cardBg2, color: T.textPrimary, border: `1px solid ${T.border2}`, borderRadius: 8, padding: '7px 12px', fontSize: 12, fontFamily: "'DM Mono', monospace", outline: 'none' }}
                />
              </div>
              {/* Body */}
              <div style={{ overflow: 'auto', flex: 1 }}>
                {logLoading ? (
                  <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
                ) : auditLog.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', color: T.textMuted, fontSize: 12 }}>No activity recorded yet.</div>
                ) : visibleLog.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', color: T.textMuted, fontSize: 12 }}>No entries match "{logSearch}"</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ background: '#f1f5f9' }}>
                        {['TIME', 'BY', 'HUB', 'RIDER', 'FHRID', 'ACTION', 'FIELD', 'OLD VALUE', 'NEW VALUE'].map(h => (
                          <th key={h} style={{ color: T.textMuted, fontSize: 9, letterSpacing: 1, padding: '10px 14px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap', borderBottom: '2px solid #e2e8f0' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleLog.map((entry, i) => (
                        <tr key={entry.id} style={{ borderTop: '1px solid #e2e8f0', background: i % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                          <td style={{ padding: '8px 14px', color: T.textMuted, whiteSpace: 'nowrap' }}>
                            {new Date(entry.done_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td style={{ padding: '8px 14px', color: T.amber, fontWeight: 700, whiteSpace: 'nowrap' }}>{entry.done_by}</td>
                          <td style={{ padding: '8px 14px', color: T.textMuted, whiteSpace: 'nowrap' }}>{entry.hub_name || '–'}</td>
                          <td style={{ padding: '8px 14px', color: T.textPrimary, whiteSpace: 'nowrap' }}>{entry.rider_name || '–'}</td>
                          <td style={{ padding: '8px 14px', color: T.textMuted, whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{entry.casper_employee_id || '–'}</td>
                          <td style={{ padding: '8px 14px', whiteSpace: 'nowrap' }}>
                            <span style={{
                              background: entry.action === 'account_fill' ? '#d1fae5' : entry.action === 'sync' ? '#dbeafe' : '#fef3c7',
                              color:      entry.action === 'account_fill' ? T.green    : entry.action === 'sync' ? T.blue    : T.amber,
                              borderRadius: 4, padding: '2px 8px', fontSize: 9, fontWeight: 700,
                            }}>{entry.action}</span>
                          </td>
                          <td style={{ padding: '8px 14px', color: T.textSecond }}>{entry.field_name || '–'}</td>
                          <td style={{ padding: '8px 14px', color: T.red, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.old_value ?? '–'}</td>
                          <td style={{ padding: '8px 14px', color: T.green, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.new_value ?? '–'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modals */}
      {showUpload && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          onUploaded={(upload, count) => {
            setUploads(prev => [upload, ...prev]);
            setSelectedId(upload.id);
            setShowUpload(false);
            alert(`✓ Uploaded ${count} rider rows for ${upload.period}`);
          }}
        />
      )}
      {accountModal && (
        <AccountModal
          rider={accountModal}
          currentUser={user}
          onClose={() => setAccountModal(null)}
          onSaved={updated => {
            logPaymentAction({
              uploadId: selectedId, riderId: updated.id,
              riderName: updated.wm_name, hubName: updated.hub_name,
              casperId: updated.casper_employee_id,
              action: 'account_fill', fieldName: 'account_details',
              oldValue: null, newValue: updated.acct_number, doneBy: actorName,
            });
            patchRider(updated.id, updated);
            // Write-back account fields to Google Sheet (fire-and-forget)
            writeBack(updated, {
              acct_holder: updated.acct_holder,
              acct_number: updated.acct_number,
              bank_name:   updated.bank_name,
              ifsc_code:   updated.ifsc_code,
              pan_no:      updated.pan_no,
            });
            setAccountModal(null);
          }}
        />
      )}

      {/* ── Header bar (compact single row) ──────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderBottom: `1px solid ${T.border}`, paddingBottom: 8 }}>
        {/* Title */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexShrink: 0 }}>
          <span style={{ color: T.textPrimary, fontSize: 15, fontWeight: 900 }}>Payment Sheet</span>
          <span style={{ color: T.textMuted, fontSize: 10 }}>ODH</span>
        </div>
        {/* Period selector */}
        {uploads.length > 0 && (
          <select value={selectedId || ''} onChange={e => setSelectedId(e.target.value)}
            style={{ ...inputStyle, maxWidth: 260 }}>
            {uploads.map(u => (
              <option key={u.id} value={u.id}>{u.period} — {u.filename}</option>
            ))}
          </select>
        )}
        {/* Right-side buttons */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginLeft: 'auto' }}>
          {fullAdmin && filtered.length > 0 && (
            <button
              onClick={() => exportPaymentRidersToExcel(filtered, calcFinalAmount, `Wheeley_Payment_${selectedUpload?.period || 'export'}_${new Date().toISOString().split('T')[0]}.xlsx`)}
              style={{ background: '#d1fae5', color: T.green, border: `1px solid ${T.green}`, borderRadius: 6, padding: '5px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              ⬇ Excel
            </button>
          )}
          {isAdmin && filtered.length > 0 && (
            <>
              <button
                onClick={() => handleBulkLock(true)}
                disabled={bulkLocking}
                title="Lock all filtered rows (skips done/paid rows)"
                style={{ background: bulkLocking ? '#f1f5f9' : '#fff7ed', color: bulkLocking ? '#9ca3af' : T.orange, border: `1px solid ${bulkLocking ? '#d1d5db' : T.orange}`, borderRadius: 6, padding: '5px 10px', fontSize: 10, fontWeight: 700, cursor: bulkLocking ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
                {bulkLocking ? '⏳' : '🔒'} Lock All
              </button>
              <button
                onClick={() => handleBulkLock(false)}
                disabled={bulkLocking}
                title="Unlock all manually locked filtered rows"
                style={{ background: 'transparent', color: bulkLocking ? '#9ca3af' : T.textMuted, border: `1px solid ${bulkLocking ? '#d1d5db' : T.border2}`, borderRadius: 6, padding: '5px 10px', fontSize: 10, fontWeight: 700, cursor: bulkLocking ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
                {bulkLocking ? '⏳' : '🔓'} Unlock All
              </button>
            </>
          )}
          {isAdmin && selectedUpload?.sheet_id && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <button onClick={handleSync} disabled={syncing}
                style={{ background: syncing ? '#f1f5f9' : '#d1fae5', color: syncing ? '#9ca3af' : T.green, border: `1px solid ${syncing ? '#d1d5db' : T.green}`, borderRadius: 6, padding: '5px 10px', fontSize: 10, fontWeight: 700, cursor: syncing ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
                {syncing ? '⏳ Syncing…' : '🔄 Sync'}
              </button>
              <span style={{ color: T.textMuted, fontSize: 9, whiteSpace: 'nowrap' }}>
                {syncing
                  ? 'Syncing now…'
                  : selectedUpload?.last_synced_at
                    ? `Last synced ${formatSyncTime(selectedUpload.last_synced_at)}`
                    : 'Never synced'}
              </span>
            </div>
          )}
          {isAdmin && duplicateExtraCount > 0 && (
            <button onClick={handleRemoveDuplicates} disabled={deduping}
              title="Same Casper Employee ID appears more than once in this upload — remove the extra copies"
              style={{ background: deduping ? '#f1f5f9' : '#fee2e2', color: deduping ? '#9ca3af' : T.red, border: `1px solid ${deduping ? '#d1d5db' : T.red}`, borderRadius: 6, padding: '5px 10px', fontSize: 10, fontWeight: 700, cursor: deduping ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
              {deduping ? '⏳ Removing…' : `🧹 ${duplicateExtraCount} Duplicate${duplicateExtraCount > 1 ? 's' : ''}`}
            </button>
          )}
          {isAdmin && nameHubExtraCount > 0 && (
            <button onClick={handleReviewNameHubDuplicates}
              title="Same name+hub more than once with no Casper ID on file — might be the same person or two different people. Review before deleting anything."
              style={{ background: 'transparent', color: T.amber, border: `1px solid ${T.amber}`, borderRadius: 6, padding: '5px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              {`⚠ ${nameHubExtraCount} Possible Dup${nameHubExtraCount > 1 ? 's' : ''} (review)`}
            </button>
          )}
          {fullAdmin && selectedUpload?.sheet_id && (
            <button onClick={handleBackfillToSheet} disabled={backfilling}
              title="Push saved app values (Pmt Decision, Remarks, deductions, account details) into the Google Sheet — for recovering after write-back was broken"
              style={{ background: backfilling ? '#f1f5f9' : '#fef3c7', color: backfilling ? '#9ca3af' : T.amber, border: `1px solid ${backfilling ? '#d1d5db' : T.amber}`, borderRadius: 6, padding: '5px 10px', fontSize: 10, fontWeight: 700, cursor: backfilling ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
              {backfilling ? `⏳ Pushing ${backfillProgress?.done ?? 0}/${backfillProgress?.total ?? 0}…` : '⏫ Backfill to Sheet'}
            </button>
          )}
          {isAdmin && (
            <>
              <button
                onClick={async () => {
                  if (!showLog) {
                    setLogLoading(true);
                    try { setAuditLog(await getPaymentAuditLog(selectedId)); } catch (e) { alert('Failed to load log: ' + e.message); }
                    setLogLoading(false);
                  }
                  setShowLog(v => !v);
                }}
                style={{ background: showLog ? '#fef3c7' : 'transparent', color: showLog ? T.amber : T.textMuted, border: `1px solid ${showLog ? T.amber : T.border2}`, borderRadius: 6, padding: '5px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                {logLoading ? '⏳' : '📋'} Log
              </button>
              {fullAdmin && (
                <button onClick={() => setShowUpload(true)}
                  style={{ background: T.amber, color: '#0f1117', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 10, fontWeight: 900, cursor: 'pointer', fontFamily: 'inherit' }}>
                  📤 Upload
                </button>
              )}
              {selectedId && fullAdmin && (
                <button onClick={() => handleDeleteUpload(selectedId)}
                  style={{ background: 'transparent', color: T.red, border: `1px solid ${T.red}`, borderRadius: 6, padding: '5px 10px', fontSize: 10, cursor: 'pointer', fontFamily: 'inherit' }}>
                  🗑
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Realtime refresh notice — shown when someone else just synced / locked ─ */}
      {refreshNotice && (
        <div style={{
          background: '#eff6ff', border: `1px solid ${T.blue}`, color: T.blue,
          borderRadius: 8, padding: '6px 12px', fontSize: 11, fontWeight: 700,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          🔄 {refreshNotice.by || 'Someone'} just {
            { sync: 'synced from Google Sheets', lock_all: 'locked all rows', unlock_all: 'unlocked all rows',
              row_lock: 'locked a row', row_unlock: 'unlocked a row' }[refreshNotice.reason] || 'updated this sheet'
          } — list refreshed automatically.
        </div>
      )}

      {/* ── Stats strip (compact) ─────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
        {[
          { label: 'RIDERS',      value: stats.total.toLocaleString('en-IN'),                               color: T.textPrimary },
          { label: 'MISSING',     value: stats.missing.toLocaleString('en-IN'),                             color: stats.missing > 0 ? T.red : T.green },
          { label: 'ATTEMPTED',   value: stats.totalAttempted.toLocaleString('en-IN'),                      color: T.textSecond },
          { label: 'DELIVERED',   value: stats.totalDelivered.toLocaleString('en-IN'),                      color: T.green },
          { label: 'CONV.',       value: stats.conversion != null ? `${stats.conversion.toFixed(1)}%` : '–', color: stats.conversion >= 70 ? T.green : stats.conversion >= 50 ? T.amber : T.red },
          { label: 'CPS',         value: stats.cps != null ? `₹${stats.cps.toFixed(2)}` : '–',            color: T.blue },
          { label: 'TOTAL AMT',   value: fmt(Math.round(stats.totalAmt)),                                   color: T.blue },
          { label: 'ADVANCE',     value: fmt(Math.round(stats.totalAdvance)),                               color: T.red },
          { label: 'COD LOSS',    value: fmt(Math.round(stats.totalCodLoss)),                               color: T.red },
          { label: 'SHIP LOSS',   value: fmt(Math.round(stats.totalShipLoss)),                              color: T.red },
          { label: 'FINAL AMT',    value: fmt(Math.round(stats.totalFinal)),   color: T.purple },
          { label: 'PAID AMT',    value: fmt(Math.round(stats.paidAmt)),     color: T.green },
          { label: 'PENDING AMT', value: fmt(Math.round(stats.pendingAmt)),  color: T.red },
        ].map(s => (
          <div key={s.label} style={{ background: T.cardBg, border: `1px solid ${T.border}`, borderRadius: 7, padding: '5px 12px', flexShrink: 0, minWidth: 80 }}>
            <div style={{ color: s.color, fontSize: 14, fontWeight: 800, lineHeight: 1.2 }}>{s.value}</div>
            <div style={{ color: T.textMuted, fontSize: 8, letterSpacing: 0.8, marginTop: 1 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Filters ────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 9, color: T.textMuted, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>Search</span>
          <input placeholder="Search rider / hub..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ ...inputStyle, width: 220 }} />
        </div>
        {/* State filter */}
        {stateOptions.length > 2 && (
          <MultiSelectFilter label="State" options={stateOptions.slice(1)} selected={filterState} onChange={setFilterState} inputStyle={inputStyle} />
        )}
        {/* Hub filter (admin/manager only) */}
        {(isAdmin || user?.role === 'manager') && hubOptions.length > 2 && (
          <MultiSelectFilter label="Hub" options={hubOptions.slice(1)} selected={filterHub} onChange={setFilterHub} inputStyle={inputStyle} />
        )}
        <MultiSelectFilter label="Active" options={['Active', 'Inactive']} selected={filterActive} onChange={setFilterActive} inputStyle={inputStyle} />
        {/* Pmt Decision filter */}
        {decisionOptions.length > 1 && (
          <MultiSelectFilter label="Pmt Decision" options={decisionOptions.slice(1)} selected={filterDecision} onChange={setFilterDecision} color={T.purple} inputStyle={inputStyle} />
        )}
        {/* Pmt Status filter */}
        {pmtStatusOptions.length > 1 && (
          <MultiSelectFilter label="Pmt Status" options={pmtStatusOptions.slice(1)} selected={filterPmtStatus} onChange={setFilterPmtStatus} color={T.green} inputStyle={inputStyle} />
        )}
        <button onClick={() => setFilterMissing(v => !v)}
          style={{ ...inputStyle, cursor: 'pointer', fontWeight: 700, letterSpacing: 1,
            background: filterMissing ? '#fee2e2' : T.inputBg,
            color: filterMissing ? T.red : T.textMuted,
            border: `1px solid ${filterMissing ? T.red : T.border2}` }}>
          {filterMissing ? '⚠ MISSING ACCT' : '○ MISSING ACCT'}
        </button>
        {(search || filterActive.length || filterMissing || filterHub.length || filterState.length || filterDecision.length || filterPmtStatus.length) && (
          <button onClick={() => { setSearch(''); setFilterActive([]); setFilterMissing(false); setFilterHub([]); setFilterState([]); setFilterDecision([]); setFilterPmtStatus([]); }}
            style={{ ...inputStyle, cursor: 'pointer', color: T.red }}>✕ Clear</button>
        )}
        <div style={{ marginLeft: 'auto', color: T.textMuted, fontSize: 12 }}>
          {filtered.length} of {riders.length} riders
        </div>
      </div>

      {/* ── No uploads state ────────────────────────────────────────────────── */}
      {uploads.length === 0 && !loading && (
        <div style={{ background: T.cardBg, border: `1px solid ${T.border}`, borderRadius: 12, padding: 48, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
          <div style={{ color: T.textMuted, fontSize: 13 }}>No payment sheets uploaded yet.</div>
          {isAdmin && (
            <button onClick={() => setShowUpload(true)} style={{ marginTop: 16, background: T.amber, color: '#ffffff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 12, fontWeight: 900, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: 1 }}>
              📤 Upload First Sheet
            </button>
          )}
        </div>
      )}

      {/* ── Table ──────────────────────────────────────────────────────────── */}
      {uploads.length > 0 && (
        <div style={{ background: T.pageBg, border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'auto', maxHeight: 'calc(100vh - 175px)' }}>
          {loading ? <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ background: T.headerBg }}>
                  {/* Identity — first two columns frozen */}
                  <th style={{ ...thStyle(), left: 0, zIndex: 4, minWidth: 160, borderRight: `1px solid ${T.border}` }}>RIDER</th>
                  {(isAdmin || user?.role === 'manager') && <th style={{ ...thStyle(), left: 160, zIndex: 4, minWidth: 130, borderRight: `2px solid ${T.border2}` }}>HUB</th>}
                  <th style={thStyle()}>STATE</th>
                  <th style={thStyle()}>STATUS</th>
                  {/* Metrics (read-only) */}
                  <th style={thStyle(T.blue)}>ATTEMPTS</th>
                  <th style={thStyle(T.blue)}>ODH DEL.</th>
                  <th style={thStyle(T.blue)}>MAN DAYS</th>
                  <th style={thStyle(T.blue)}>PROP. RATE</th>
                  <th style={thStyle(T.blue)}>AMOUNT</th>
                  <th style={thStyle(T.red)}>PENALTY</th>
                  {/* Ground team editable */}
                  <th style={thStyle(T.green)}>ADVANCE</th>
                  <th style={thStyle(T.green)}>COD LOSS</th>
                  <th style={thStyle(T.green)}>SHIP LOSS</th>
                  {/* Control-tower-only (not ground team) */}
                  <th style={thStyle(T.orange)}>SHIP LOSS 2</th>
                  <th style={thStyle(T.orange)}>SCOOTY</th>
                  {/* Computed */}
                  <th style={thStyle('#a78bfa')}>TDS</th>
                  <th style={thStyle('#a78bfa')}>FINAL AMT</th>
                  {/* Ground team editable */}
                  <th style={thStyle('#fbbf24')}>PMT DECISION</th>
                  <th style={thStyle('#fbbf24')}>REMARKS</th>
                  {/* PMT Status (admin editable) */}
                  <th style={thStyle('#f59e0b')}>PMT STATUS</th>
                  {/* PAN-Aadhaar link status — read-only, from the sheet's STATUS column */}
                  <th style={thStyle('#f97316')}>PAN STATUS</th>
                  {/* Account */}
                  <th style={thStyle('#f97316')}>ACCOUNT</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={21} style={{ color: T.textMuted, textAlign: 'center', padding: 40 }}>No riders match the filters.</td></tr>
                ) : filtered.map((r, i) => {
                  const hasAcct    = accountComplete(r);
                  const isExpanded = expandedId === r.id;
                  const finalAmt   = calcFinalAmount(r);
                  const isDoneLocked   = isPaidStatus(r);
                  const isManualLocked = !!r.is_locked && !isDoneLocked; // manual lock (not yet paid)
                  const isLocked       = isDoneLocked || isManualLocked;
                  const isHold         = isAdmin && r.pmt_hold?.toLowerCase() === 'hold';
                  const canEdit        = !isLocked && (isAdmin || user?.role === 'manager' || sameHub(user?.hub_name, r.hub_name));
                  // Shipment Loss 2 and Scooty Rent are control-tower-only fields (added
                  // 2026-10-22) — ground team (hub users) must not be able to edit them,
                  // even for their own hub. isAdmin covers both real Admin and Control
                  // Tower (see the isAdmin/fullAdmin note at the top of this file).
                  const canEditRestricted = !isLocked && isAdmin;
                  const rowBg          = isDoneLocked ? '#f0fdf4' : isManualLocked ? '#fff7ed' : isHold ? '#fdf2f8' : isExpanded ? T.rowExpanded : i % 2 === 0 ? T.rowEven : T.rowOdd;

                  return (
                    <>
                    <tr key={r.id}
                      style={{ borderTop: `1px solid ${T.border}`, background: rowBg, cursor: 'pointer', lineHeight: 1 }}
                      onClick={() => setExpandedId(isExpanded ? null : r.id)}>

                      {/* RIDER NAME — frozen column 1 */}
                      <td style={{ padding: '5px 10px', whiteSpace: 'nowrap', position: 'sticky', left: 0, zIndex: 1, background: rowBg, borderRight: `1px solid ${T.border}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: T.textMuted, fontSize: 9, transform: isExpanded ? 'rotate(90deg)' : 'none', display: 'inline-block', transition: 'transform .15s', marginRight: 4 }}>▶</span>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                              <span style={{ color: T.textPrimary, fontWeight: 600, fontSize: 11 }}>{r.wm_name}</span>
                              {isDoneLocked && <span style={{ background: '#d1fae5', color: T.green, borderRadius: 3, padding: '0px 5px', fontSize: 7, fontWeight: 700, letterSpacing: 0.5 }}>PAID ✓</span>}
                              {isManualLocked && <span style={{ background: '#ffedd5', color: T.orange, borderRadius: 3, padding: '0px 5px', fontSize: 7, fontWeight: 700, letterSpacing: 0.5 }}>🔒 LOCKED</span>}
                              {isHold && <span style={{ background: '#fce7f3', color: '#be185d', borderRadius: 3, padding: '0px 5px', fontSize: 7, fontWeight: 700, letterSpacing: 0.5 }}>⏸ HOLD</span>}
                              {/* Hold toggle — admin only */}
                              {isAdmin && (
                                <button
                                  onClick={async e => {
                                    e.stopPropagation();
                                    const newHold = !isHold;
                                    try {
                                      await updatePaymentRiderHold(r.id, newHold);
                                      patchRider(r.id, { pmt_hold: newHold ? 'Hold' : null });
                                    } catch (err) {
                                      alert('Hold update failed: ' + err.message);
                                    }
                                  }}
                                  style={{
                                    background: isHold ? '#fce7f3' : 'transparent',
                                    color: isHold ? '#be185d' : T.textMuted,
                                    border: `1px solid ${isHold ? '#be185d' : T.border2}`,
                                    borderRadius: 4, padding: '0px 5px', fontSize: 9, fontWeight: 700,
                                    cursor: 'pointer', fontFamily: 'inherit', lineHeight: '14px',
                                  }}
                                  title={isHold ? 'Release hold (show to ground team)' : 'Put on hold (hide from ground team)'}
                                >
                                  {isHold ? '▶' : '⏸'}
                                </button>
                              )}
                              {/* Lock / Unlock button — admin only, not shown for done-locked rows */}
                              {isAdmin && !isDoneLocked && (
                                <button
                                  onClick={async e => {
                                    e.stopPropagation();
                                    const newLocked = !isManualLocked;
                                    try {
                                      await updatePaymentRiderLock(r.id, newLocked);
                                      patchRider(r.id, { is_locked: newLocked });
                                      logPaymentAction({
                                        uploadId: selectedId, riderId: r.id,
                                        riderName: r.wm_name, hubName: r.hub_name,
                                        casperId: r.casper_employee_id,
                                        action: newLocked ? 'manual_lock' : 'manual_unlock',
                                        fieldName: 'is_locked', oldValue: String(!newLocked), newValue: String(newLocked),
                                        doneBy: actorName,
                                      });
                                      broadcastRefresh(newLocked ? 'row_lock' : 'row_unlock');
                                    } catch (err) {
                                      alert('Lock failed: ' + err.message + '\n\nMake sure you have run the SQL migration: ALTER TABLE payment_riders ADD COLUMN IF NOT EXISTS is_locked BOOLEAN NOT NULL DEFAULT FALSE;');
                                    }
                                  }}
                                  style={{
                                    background: isManualLocked ? '#fff7ed' : 'transparent',
                                    color: isManualLocked ? T.orange : T.textMuted,
                                    border: `1px solid ${isManualLocked ? T.orange : T.border2}`,
                                    borderRadius: 4, padding: '0px 5px', fontSize: 9, fontWeight: 700,
                                    cursor: 'pointer', fontFamily: 'inherit', lineHeight: '14px',
                                  }}
                                  title={isManualLocked ? 'Unlock this row' : 'Lock this row'}
                                >
                                  {isManualLocked ? '🔓' : '🔒'}
                                </button>
                              )}
                            </div>
                            {r.casper_employee_id && <div style={{ color: T.textMuted, fontSize: 9 }}>{r.casper_employee_id}</div>}
                          </div>
                        </div>
                      </td>

                      {/* HUB — frozen column 2 (admin/manager only) */}
                      {(isAdmin || user?.role === 'manager') && (
                        <td style={{ padding: '5px 10px', color: T.textSecond, whiteSpace: 'nowrap', position: 'sticky', left: 160, zIndex: 1, background: rowBg, borderRight: `2px solid ${T.border2}`, fontSize: 11 }}>{r.hub_name}</td>
                      )}

                      {/* STATE */}
                      <td style={{ padding: '5px 10px', color: '#6b7280', whiteSpace: 'nowrap', fontSize: 11 }}>{r.state || '–'}</td>

                      {/* STATUS badges */}
                      <td style={{ padding: '5px 10px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <span style={{ background: r.currently_active === 'Active' ? '#d1fae5' : '#fee2e2', color: r.currently_active === 'Active' ? T.green : T.red, borderRadius: 4, padding: '1px 7px', fontSize: 9, fontWeight: 700 }}>
                            {r.currently_active || '—'}
                          </span>
                          {r.pmt_hold && (
                            <span style={{ background: '#ffedd5', color: T.orange, borderRadius: 4, padding: '1px 7px', fontSize: 9, fontWeight: 700 }}>
                              {r.pmt_hold}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Metrics (read-only) */}
                      <td style={tdNum()}>{r.total_attempted ?? '–'}</td>
                      <td style={tdNum()}>{r.odh_delivered ?? '–'}</td>
                      <td style={tdNum()}>{r.man_days ?? '–'}</td>
                      <td style={tdNum(T.blue)}>₹{r.proposed_rate ?? '–'}</td>
                      <td style={tdNum(T.blue)}>{r.amount != null ? fmt(r.amount) : '–'}</td>
                      <td style={tdNum(r.penalty ? T.red : undefined)}>{r.penalty ? fmt(Math.round(r.penalty)) : '–'}</td>

                      {/* Ground team editable: numeric */}
                      {['advance','cod_loss','shipment_loss'].map(field => (
                        <td key={field} style={{ padding: '4px 8px' }} onClick={e => e.stopPropagation()}>
                          <EditCell
                            value={r[field]}
                            color={T.green}
                            readOnly={!canEdit}
                            maxValue={maxDeductionValue(r, field)}
                            onSave={v => handleFieldSave(r.id, field, v)}
                          />
                        </td>
                      ))}

                      {/* Control-tower-only: numeric (ground team cannot edit, see canEditRestricted) */}
                      {['shipment_loss2','scooty_rent'].map(field => (
                        <td key={field} style={{ padding: '4px 8px' }} onClick={e => e.stopPropagation()}>
                          <EditCell
                            value={r[field]}
                            color={T.orange}
                            readOnly={!canEditRestricted}
                            maxValue={maxDeductionValue(r, field)}
                            onSave={v => handleFieldSave(r.id, field, v)}
                          />
                        </td>
                      ))}

                      {/* Computed */}
                      <td style={tdNum(T.purple)}>{r.tds != null ? fmt(Math.round(r.tds)) : '–'}</td>
                      <td style={{ padding: '4px 8px', color: finalAmt >= 0 ? T.purple : T.red, fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {fmt(Math.round(finalAmt))}
                      </td>

                      {/* PMT Decision + Remarks (text editable) */}
                      <td style={{ padding: '4px 8px' }} onClick={e => e.stopPropagation()}>
                        <TextCell
                          value={r.pmt_decision}
                          color="#fbbf24"
                          readOnly={!canEdit}
                          options={['Yes', 'No', 'Hold']}
                          onSave={v => handleFieldSave(r.id, 'pmt_decision', v)}
                        />
                      </td>
                      <td style={{ padding: '4px 8px' }} onClick={e => e.stopPropagation()}>
                        <TextCell
                          value={r.remarks}
                          color="#fbbf24"
                          readOnly={!canEdit}
                          onSave={v => handleFieldSave(r.id, 'remarks', v)}
                        />
                      </td>

                      {/* PMT Status (read-only — updated via Google Sheet sync only) */}
                      <td style={{ padding: '4px 8px' }}>
                        <span style={{ color: r.pmt_status ? T.amber : T.textMuted, fontSize: 11 }}>
                          {r.pmt_status || '–'}
                        </span>
                      </td>

                      {/* PAN-Aadhaar link status — read-only, visible to everyone including
                          ground team so they can see whose PAN needs correcting/linking */}
                      <td style={{ padding: '4px 8px' }}>
                        <span style={{
                          background: r.pan_status === 'Valid and Operative' ? '#d1fae5'
                                    : r.pan_status ? '#fee2e2' : 'transparent',
                          color: r.pan_status === 'Valid and Operative' ? T.green
                               : r.pan_status ? T.red : T.textMuted,
                          borderRadius: 4, padding: '1px 7px', fontSize: 10, fontWeight: r.pan_status ? 700 : 400,
                          whiteSpace: 'nowrap',
                        }}>
                          {r.pan_status || '–'}
                        </span>
                      </td>

                      {/* Account status — only admin can open/edit the modal */}
                      <td style={{ padding: '4px 8px' }} onClick={e => e.stopPropagation()}>
                        {isAdmin ? (
                          isLocked ? (
                            <button onClick={() => setAccountModal(r)}
                              style={{ background: '#f1f5f9', color: T.textMuted, border: `1px solid ${T.border2}`, borderRadius: 6, padding: '3px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                              🔒 View
                            </button>
                          ) : hasAcct ? (
                            <button onClick={() => setAccountModal(r)}
                              style={{ background: '#d1fae5', color: T.green, border: `1px solid ${T.green}`, borderRadius: 6, padding: '3px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                              🔒 View
                            </button>
                          ) : (
                            <button onClick={() => setAccountModal(r)}
                              style={{ background: '#fee2e2', color: T.red, border: `1px solid ${T.red}`, borderRadius: 6, padding: '3px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                              ⚠ Fill
                            </button>
                          )
                        ) : (
                          /* Hub / manager: read-only badge, no modal */
                          hasAcct ? (
                            <span style={{ background: '#d1fae5', color: T.green, border: `1px solid ${T.green}`, borderRadius: 6, padding: '3px 10px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>
                              ✓ On File
                            </span>
                          ) : (
                            <span style={{ background: '#fee2e2', color: T.red, border: `1px solid ${T.red}`, borderRadius: 6, padding: '3px 10px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>
                              ⚠ Missing
                            </span>
                          )
                        )}
                      </td>
                    </tr>

                    {/* ── Expanded row: extra details ── */}
                    {isExpanded && (
                      <tr key={`${r.id}-exp`}>
                        <td colSpan={21} style={{ background: T.cardBg2, borderTop: `1px solid ${T.border}`, padding: '12px 24px' }}>
                          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', fontSize: 11 }}>
                            <div>
                              <div style={{ color: T.textMuted, marginBottom: 6, letterSpacing: 1, fontSize: 9 }}>METRICS</div>
                              {[
                                ['GTNL Delivered', r.gtnl_delivered],
                                ['U2S', r.u2s],
                                ['Shopsy', r.shopsy_delivered],
                                ['Conversion', r.conversion != null ? (Number(r.conversion) * 100).toFixed(1) + '%' : null],
                                ['Shopsy Rate', r.shopsy_rate_card],
                                ['Proposed Rate', r.proposed_rate],
                                ['Mandays Inc', r.mandays_inc],
                              ].map(([k, v]) => v != null && (
                                <div key={k} style={{ color: T.textSecond, marginBottom: 2 }}>{k}: <span style={{ color: T.textPrimary }}>{v}</span></div>
                              ))}
                            </div>
                            <div>
                              <div style={{ color: T.textMuted, marginBottom: 6, letterSpacing: 1, fontSize: 9 }}>ACCOUNT DETAILS</div>
                              {hasAcct ? (
                                <>
                                  <div style={{ color: T.textSecond, marginBottom: 2 }}>Holder: <span style={{ color: T.textPrimary }}>{r.acct_holder || '–'}</span></div>
                                  <div style={{ color: T.textSecond, marginBottom: 2 }}>Account: <span style={{ color: T.textPrimary }}>{r.acct_number}</span></div>
                                  <div style={{ color: T.textSecond, marginBottom: 2 }}>Bank: <span style={{ color: T.textPrimary }}>{r.bank_name}</span></div>
                                  <div style={{ color: T.textSecond, marginBottom: 2 }}>IFSC: <span style={{ color: T.textPrimary }}>{r.ifsc_code}</span></div>
                                  <div style={{ color: T.textSecond, marginBottom: 2 }}>PAN: <span style={{ color: T.textPrimary }}>{r.pan_no || '–'}</span></div>
                                  {r.acct_updated_by && <div style={{ color: T.textMuted, fontSize: 10, marginTop: 4 }}>Filled by {r.acct_updated_by}</div>}
                                </>
                              ) : (
                                <div style={{ color: T.red }}>⚠ Missing{isAdmin ? ' — click "Fill" to add' : ''}</div>
                              )}
                            </div>
                            <div>
                              <div style={{ color: T.textMuted, marginBottom: 6, letterSpacing: 1, fontSize: 9 }}>DEDUCTIONS</div>
                              {[
                                ['Advance', r.advance],
                                ['COD Loss', r.cod_loss],
                                ['Shipment Loss', r.shipment_loss],
                                ['Scooty Rent', r.scooty_rent],
                                ['Rider Welfare', r.rider_welfare],
                                ['TDS', r.tds],
                                ['Penalty', r.penalty],
                              ].map(([k, v]) => (
                                <div key={k} style={{ color: T.textSecond, marginBottom: 2 }}>{k}: <span style={{ color: v ? T.red : T.textMuted }}>{v ? fmt(v) : '–'}</span></div>
                              ))}
                            </div>
                            {r.remarks1 || r.sheet_reference || r.pan_card_check ? (
                              <div>
                                <div style={{ color: T.textMuted, marginBottom: 6, letterSpacing: 1, fontSize: 9 }}>OTHER</div>
                                {r.remarks1 && <div style={{ color: T.textSecond, marginBottom: 2 }}>Remarks1: <span style={{ color: T.textPrimary }}>{r.remarks1}</span></div>}
                                {r.sheet_reference && <div style={{ color: T.textSecond, marginBottom: 2 }}>Sheet Ref: <span style={{ color: T.textPrimary }}>{r.sheet_reference}</span></div>}
                                {r.pan_card_check && <div style={{ color: T.textSecond, marginBottom: 2 }}>PAN Check: <span style={{ color: T.textPrimary }}>{r.pan_card_check}</span></div>}
                                {r.last_time_rate != null && <div style={{ color: T.textSecond, marginBottom: 2 }}>Last Rate: <span style={{ color: T.textPrimary }}>₹{r.last_time_rate}</span></div>}
                              </div>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    )}
                    </>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Legend ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 9, color: T.textSecond }}>
        <span style={{ color: T.blue }}>● Blue = read-only metrics</span>
        <span style={{ color: T.green }}>● Green = click to edit (your hub)</span>
        <span style={{ color: '#fbbf24' }}>● Yellow = PMT decision / remarks</span>
        <span style={{ color: '#a78bfa' }}>● Purple = computed final amount</span>
        <span style={{ color: '#f97316' }}>● Orange = account details</span>
        <span style={{ color: '#f59e0b' }}>● Gold = PMT status (read-only, updated via Google Sheet)</span>
      </div>
    </div>
  );
}

// ── Style helpers ─────────────────────────────────────────────────────────────
function thStyle(color = '#6b7280') {
  return { color, fontSize: 8, letterSpacing: 1, padding: '6px 10px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap', position: 'sticky', top: 0, background: T.headerBg, zIndex: 2, borderBottom: `2px solid ${T.border}` };
}
function tdNum(color = T.textSecond) {
  return { padding: '5px 10px', color, fontSize: 11, whiteSpace: 'nowrap' };
}
